import { useCallback, useEffect, useMemo, useState } from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import {
  NO_FISCAL_DEVICE_VALUE,
  type Device,
  type DevicePaymentMethod,
} from "../../../../../api/devices";
import type { UserListItemOut } from "../../../../../api/auth";
import { useViewport } from "../../../../../hooks/useViewport";
import { usePaymentMethods } from "../../../../../hooks/usePaymentMethods";
import { useUnsavedChangesGuard } from "../../panel/useUnsavedChangesGuard";
import { deviceFormSnapshot } from "../deviceFormSnapshot";
import { validateDevicePaymentMethods } from "../devicePaymentMethodsValidation";
import type { DeviceFormValues } from "../DeviceFormModal";
import { buildDeviceAccounts } from "../modal/deviceAccounts";
import DevicePhoneTabs from "../modal/DevicePhoneTabs";
import DeviceModalShell, { DeviceSummaryItem } from "../modal/DeviceModalShell";
import { FiscalReceiptIcon } from "../modal/deviceModalIcons";
import FiscalAccountsSection from "./FiscalAccountsSection";
import FiscalPaymentMethodsSection from "./FiscalPaymentMethodsSection";
import FiscalReceiptSection from "./FiscalReceiptSection";

interface Props {
  open: boolean;
  // Създаване (device=null) или редакция.
  device: Device | null;
  users: UserListItemOut[];
  devices: Device[];
  // Новият GUID при създаване идва от извикващия (общ с другите типове).
  newGuid: () => string;
  // Извикващият прави мутацията и toast-овете; при грешка модалът остава отворен.
  onSubmit: (values: DeviceFormValues) => Promise<void>;
  onCancel: () => void;
}

type PhoneTab = "accounts" | "methods" | "receipt";

// Метод „Смесено" (id 3) няма собствен слот — фискалният му payload се строи
// от слотовете на методи 1 и 2.
const MIXED_PAYMENT_METHOD_ID = 3;

// Полето за слот: по-ниско, а при слот — зелено. Вътрешни antd класове,
// недостижими за Tailwind.
const FiscalSlotSelectCss = () => (
  <style>{`
    .cfg-form .fiscal-pm .ant-select-selector {
      min-height: 34px !important;
      height: 34px !important;
      font-size: 12px;
    }
    .cfg-form .fiscal-pm .ant-select-selection-item {
      line-height: 32px !important;
    }
    .cfg-form .fiscal-pm-slot .ant-select-selector {
      background: var(--color-ok-tint) !important;
      border-color: color-mix(in srgb, var(--color-ok) 40%, transparent) !important;
      font-weight: 600;
    }
  `}</style>
);

/**
 * Модалът на фискален принтер (създаване и редакция) — секции „Касови
 * бележки" (акаунти + статус), „Методи на плащане" и „Касова бележка".
 * Логиката е тази на досегашната обща форма: изборът на акаунт при
 * „Неактивно" включва устройството, методите се валидират преди запис.
 */
const FiscalPrinterModal = ({ open, device, users, devices, newGuid, onSubmit, onCancel }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isNarrow, isTablet } = useViewport();
  const { data: allPaymentMethods = [] } = usePaymentMethods();
  const isEdit = device !== null;

  const accounts = useMemo(
    () => buildDeviceAccounts(users, devices, "fiscal", device?.id ?? null),
    [users, devices, device],
  );
  const editablePaymentMethods = useMemo(
    () => allPaymentMethods.filter((paymentMethod) => paymentMethod.id !== MIXED_PAYMENT_METHOD_ID),
    [allPaymentMethods],
  );

  // Заварено устройство без акаунти тръгва „Неактивно".
  const initiallyEnabled = !isEdit || device.userIds.length > 0;

  const [name, setName] = useState(device?.name ?? "");
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>(device?.userIds ?? []);
  const [paymentMethods, setPaymentMethods] = useState<DevicePaymentMethod[]>(
    device?.paymentMethods ?? [],
  );
  const [summarizeReceipt, setSummarizeReceipt] = useState(device?.summarizeReceipt ?? false);
  const [nonFiscalCopy, setNonFiscalCopy] = useState(device?.nonFiscalCopy ?? false);
  const [isEnabled, setIsEnabled] = useState(initiallyEnabled);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // На телефон секциите са раздели — вижда се едната.
  const [phoneTab, setPhoneTab] = useState<PhoneTab>("accounts");

  const { requestClose, markPristine, unsavedDialog } = useUnsavedChangesGuard({
    open,
    snapshot: deviceFormSnapshot({
      name,
      userIds: selectedUserIds,
      stationIds: [],
      printLanguage: null,
      paymentMethods,
      summarizeReceipt,
      nonFiscalCopy,
      isEnabled,
    }),
    isSubmitting,
    onClose: onCancel,
  });

  useEffect(() => {
    if (!open) return;
    setName(device?.name ?? "");
    setSelectedUserIds(device?.userIds ?? []);
    setPaymentMethods(device?.paymentMethods ?? []);
    setSummarizeReceipt(device?.summarizeReceipt ?? false);
    setNonFiscalCopy(device?.nonFiscalCopy ?? false);
    setIsEnabled(initiallyEnabled);
    setPhoneTab("accounts");
    markPristine();
  }, [open, device, initiallyEnabled, markPristine]);

  // Избор на акаунт при изключен статус го включва сам — не се налага първо
  // да се пипа превключвателят.
  // Стабилна референция — секцията е memo.
  const handleAccountsChange = useCallback((ids: number[]) => {
    setSelectedUserIds(ids);
    if (ids.length > 0) setIsEnabled(true);
  }, []);

  // Създаването изисква акаунт; редакцията може да „изпразни" устройството.
  const hasAssignment = selectedUserIds.length > 0;
  const canSave = name.trim().length > 0 && !isSubmitting && (isEdit || hasAssignment);

  const handleSave = async () => {
    if (!canSave) return;
    const validationError = validateDevicePaymentMethods(paymentMethods);
    if (validationError) {
      message.warning(
        t(
          validationError === "values"
            ? "devicePaymentMethods.invalidValueError"
            : "devicePaymentMethods.duplicateMethodsError",
        ),
      );
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({
        guid: device?.guid ?? newGuid(),
        name: name.trim(),
        type: "fiscal",
        userIds: isEnabled ? selectedUserIds : [],
        stationIds: [],
        printLanguage: null,
        summarizeReceipt,
        nonFiscalCopy,
        paymentMethods,
      });
    } catch {
      // Toast-ът за грешка е при извикващия; модалът остава отворен.
    } finally {
      setIsSubmitting(false);
    }
  };

  const chosenCount = accounts.filter(
    (account) => account.takenBy === null && selectedUserIds.includes(account.id),
  ).length;
  const fiscalizedCount = paymentMethods.filter(
    (paymentMethod) =>
      editablePaymentMethods.some((editable) => editable.id === paymentMethod.paymentMethodId) &&
      paymentMethod.value !== NO_FISCAL_DEVICE_VALUE,
  ).length;

  return (
    <>
      <DeviceModalShell
        open={open}
        onRequestClose={requestClose}
        icon={<FiscalReceiptIcon size={20} />}
        kicker={isEdit ? t("devices.fiscalModal.kickerEdit") : t("devices.fiscalModal.kickerCreate")}
        phoneKicker={t("devices.typeFiscal")}
        name={name}
        onNameChange={setName}
        autoFocusName={!isEdit}
        phoneTabs={
          <DevicePhoneTabs
            activeTab={phoneTab}
            onTabChange={setPhoneTab}
            tabs={[
              {
                id: "accounts",
                label: t("devices.fiscalModal.tabAccounts"),
                count: isEnabled ? String(chosenCount) : t("devices.fiscalModal.tabOff"),
                countClassName: "bg-brand-tint text-brand-deep-ink",
              },
              {
                id: "methods",
                label: t("devices.fiscalModal.tabMethods"),
                count: String(fiscalizedCount),
                countClassName: "bg-ok-tint text-ok-deep",
              },
              {
                id: "receipt",
                label: t("devices.fiscalModal.tabReceipt"),
                countClassName: "",
              },
            ]}
          />
        }
        summary={
          <>
            <DeviceSummaryItem
              dotClassName="bg-brand"
              label={t("devices.fiscalModal.summaryReceipts")}
              value={
                isEnabled
                  ? t("devices.fiscalModal.summaryAccounts", { count: chosenCount })
                  : t("devices.fiscalModal.summaryOff")
              }
            />
            <DeviceSummaryItem
              dotClassName="bg-ok"
              label={t("devices.fiscalModal.summaryMethods")}
              value={t("devices.fiscalModal.summaryFiscalized", { count: fiscalizedCount })}
            />
          </>
        }
        saveLabel={isEdit ? t("devices.saveChanges") : t("devices.addDevice")}
        canSave={canSave}
        onSave={handleSave}
        // Десктоп: три колони (по 360px), за да се събере без скрол; таблет —
        // една колона (две по 360px там са твърде тесни).
        gridClassName={
          isTablet ? "grid-cols-1" : "grid-cols-[repeat(auto-fit,minmax(min(360px,100%),1fr))]"
        }
      >
        <FiscalSlotSelectCss />
        {(!isNarrow || phoneTab === "accounts") && (
          <FiscalAccountsSection
            accounts={accounts}
            selectedIds={selectedUserIds}
            onSelectedChange={handleAccountsChange}
            showStatus={isEdit}
            isEnabled={isEnabled}
            onEnabledChange={setIsEnabled}
            isNarrow={isNarrow}
          />
        )}
        {(!isNarrow || phoneTab === "methods") && (
          <FiscalPaymentMethodsSection
            paymentMethods={editablePaymentMethods}
            value={paymentMethods}
            onChange={setPaymentMethods}
            // При създаване принтерът още не е обявил слотове → менюто
            // съдържа само „БЕЗ ФИСКАЛИЗИРАНЕ" — честното състояние.
            slots={device?.paymentTypes?.slots ?? null}
            isNarrow={isNarrow}
          />
        )}
        {(!isNarrow || phoneTab === "receipt") && (
          <FiscalReceiptSection
            summarizeReceipt={summarizeReceipt}
            onSummarizeReceiptChange={setSummarizeReceipt}
            nonFiscalCopy={nonFiscalCopy}
            onNonFiscalCopyChange={setNonFiscalCopy}
            isNarrow={isNarrow}
          />
        )}
      </DeviceModalShell>

      {unsavedDialog}
    </>
  );
};

export default FiscalPrinterModal;
